import { TX_MSG, calculateFeeWithMinimalDenom, isSelectedCoinMinimalDenom } from '@/wallet-utils'
import { assert } from '@/utils'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { useMutation } from '@tanstack/react-query'
import { useStake } from '../../StakeProvider'
import { MutationParameters } from './types'
import { useSelectedChain } from '@/contexts/wallet'
import { flushSync } from 'react-dom'
import { useSelectedDenom } from '../../../../contexts/denom'
import { CHAIN_INFO_LIST } from '@/config'

const useSignRedeemTokenForSharesMutation = ({ setRedeemTokensRaw }: MutationParameters) => {
  const [denom] = useSelectedDenom()

  const { address: selectAddress, getSigningStargateClient } = useSelectedChain()

  const { withdrawLsm } = useStake()

  const handleMutation = async (): Promise<TxRaw> => {
    assert(selectAddress, `Unable to redeem tokens for shares without connecting your ${denom} wallet.`)

    // The user has the option to reverts from either a TokenizeShares or LSM IBC transaction
    const tokenizedDenomToRedeem = withdrawLsm.txHistoryIbcTokenizedDenom || withdrawLsm.txHistoryTokenizedDenom

    const msgRedeemTokensForShares = {
      typeUrl: TX_MSG.MsgRedeemTokensForShares,
      value: {
        amount: {
          amount: withdrawLsm.amount,
          denom: tokenizedDenomToRedeem
        },
        delegatorAddress: selectAddress
      }
    }

    const minimalDenom = CHAIN_INFO_LIST[denom].stakeCurrency.coinMinimalDenom

    assert(isSelectedCoinMinimalDenom(minimalDenom), `Invalid minimal denom for ${denom}`)

    const client = await getSigningStargateClient()
    const gas = await client.simulate(selectAddress, [msgRedeemTokensForShares], '')
    const fee = calculateFeeWithMinimalDenom(minimalDenom, gas)

    return await client.sign(selectAddress, [msgRedeemTokensForShares], fee, '')
  }

  const handleSuccess = (raw: TxRaw) => {
    flushSync(() => {
      setRedeemTokensRaw(raw)
    })
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useSignRedeemTokenForSharesMutation }
